import React, {useEffect, useState} from 'react'

import {useTranslation} from "react-i18next"
import {useParams, useSearchParams, useNavigate} from 'react-router-dom'

import AssignedShipments from '../../components/driver/AssignedShipments'
import AssignedCollections from '../../components/driver/AssignedCollections'
import AppLayout from '../../components/Layouts/AppLayout'
import {useStateContext} from '../../context/ContextProvider'
import axios from '../../lib/axios'
import Loading from '../../components/Loading'
import { handleAxiosError, isValid, automaticallyAcceptBrokerOrders } from '../../helpers/helper'
import Swal from 'sweetalert2'
import { useAuth } from '../../hooks/auth'
import { useLocation } from 'react-router-dom'
import { has } from 'lodash'

const AvailableLoadsExternal = () => {

    const { user } = useAuth({ middleware: 'guest' })
    const { t } = useTranslation()
    const params = useParams()
    const [searchParams, setSearchParams] = useSearchParams();
    const { config, pusher, choosesite, features } = useStateContext()
    const location = useLocation()
    const { pathname } = location
    const navigate = useNavigate()

    const [isLoading, setIsLoading] = useState(false)
    const [shipments, setShipments] = useState([])
    const [collections, setCollections] = useState([])

    useEffect(() => {
        searchParams.delete('code')
        setSearchParams(searchParams)
        const keyExists = sessionStorage.getItem('is_initial_redirect') !== null;
        const initialRedirect = !keyExists;
        sessionStorage.setItem('is_initial_redirect', initialRedirect);

        return (() => {
            pusher.unsubscribe(`${localStorage.getItem('client_id')}-salesorder-deleted-site-${localStorage.getItem('site')}`)
        })
    }, [])

    useEffect(() => {
        if (isValid(choosesite)) {
            const channelShipmentDelete = pusher.subscribe(`${localStorage.getItem('client_id')}-salesorder-deleted-site-${choosesite}`)
            const channelCollectionDelete = pusher.subscribe(`${localStorage.getItem('client_id')}-purchaseorder-deleted-site-${choosesite}`)

            channelShipmentDelete.bind(`${localStorage.getItem('client_id')}-salesorder-deleted-event-site-${choosesite}`, data => {
                setShipments((prev) => {
                    const target = prev.find((t) => t.id == data.id)
                    if(target) {
                        Swal.fire({
                            icon: "info",
                            iconColor: '#a8d5f1',
                            customClass: 'info',
                            timer: 3000,
                            timerProgressBar: true,
                            showConfirmButton: false,
                            text: data.message_localized ?? data.message
                        })

                        return prev.filter((s) => s.id !== target.id)
                    } else {
                        return prev
                    }
                })
            })

            channelCollectionDelete.bind(`${localStorage.getItem('client_id')}-purchaseorder-deleted-event-site-${choosesite}`, data => {
                setCollections((prev) => {
                    const target = prev.find((t) => t.id == data.id)
                    if(target) {
                        Swal.fire({
                            icon: "info",
                            iconColor: '#a8d5f1',
                            customClass: 'info',
                            timer: 3000,
                            timerProgressBar: true,
                            showConfirmButton: false,
                            text: data.message_localized ?? data.message
                        })

                        return prev.filter((s) => s.id !== target.id)
                    } else {
                        return prev
                    }
                })
            })
        }

        return (() => {
            if(isValid(choosesite)) {
                pusher.unsubscribe(`${localStorage.getItem('client_id')}-salesorder-deleted-site-${choosesite}`)
                pusher.unsubscribe(`${localStorage.getItem('client_id')}-purchaseorder-deleted-site-${choosesite}`)
            }
        })
    }, [choosesite])

    useEffect(() => {
        switch(user?.role) {
            case "tpl_driver":
                getTPLOrder()
                break
            case "service_provider":
                getBrokerTransactionOrder()
                break
        }
    }, [user])

    useEffect(() => {
        if(automaticallyAcceptBrokerOrders(features)) {
            const initialRedirect = sessionStorage.getItem('is_initial_redirect');
            if(initialRedirect === 'true') {
                if(shipments.length > 0) {
                    acceptShipmentHandler(shipments[0].id);
                } else if (collections.length > 0) {
                    navigate(`/collections/${collections[0].id}`)
                }

                if(shipments.length > 0 || collections.length > 0) {
                    setTimeout(() => {
                        sessionStorage.setItem('is_initial_redirect', false);
                    }, 333)
                }
            }

        }
    }, [shipments, collections, features])

    const getTPLOrder = async () => {
        setIsLoading(true)
        await axios.get(`/api/3pl/3pl-order?tpl_access_code=${params.id}&with=brokerTransaction`, config)
            .then(res => {
                const data = res.data

                setShipments([data])
            })
            .catch(({ response }) => {
                handleAxiosError({response: response})
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const getBrokerTransactionOrder = async () => {
        const url = `/api/broker-transaction-order?service_provider_access_code=${params.id}`

        setIsLoading(true)
        await axios.get(url, config)
            .then(res => {
                const data = res.data
                const { resource, opposing_items } = data

                if(data.type === 'shipment') {
                    setShipments([resource])
                } else if (data.type === 'collection') {
                    setCollections([resource])
                }
            })
            .catch(({ response }) => {
                handleAxiosError({response: response})
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const acceptShipmentHandler = async (id) => {
        setIsLoading(true)
        await axios.put(`/api/driver/accept-shipment/${id}`, config)
            .then(({ data }) => {
                navigate(`/shipments/${id}`)
            }).catch(({ response }) => {
                if(has(response, 'data.error.description') && ["Shipment Already Accepted.", "Envio Ya Aceptado."].includes(response.data.error.description)) {
                    navigate(`/shipments/${id}`)
                } else {
                    handleAxiosError({ response: response })
                }
            }).finally(() => {
                setIsLoading(false)
            })
    }

    return (
        <>
        {isLoading ? <Loading /> : null}
        <AppLayout>
            <div className='flex flex-col justify-between'>
                {shipments.length > 0 && (
                    <div className='p-5 w-full'>
                        <div className='bg-white mb-2 rounded-md'>
                            <div className='p-5 flex justify-between items-center border-b'>
                                <div className='flex justify-start gap-4 items-center'>
                                    <p style={{ fontWeight: 600, fontSize: '16px' }}>{t('shipments')}</p>
                                </div>
                            </div>
                            <div className='pt-3'>
                                <AssignedShipments shipments={shipments ?? []} user={user} />
                            </div>
                        </div>
                    </div>
                )}

                {collections.length > 0 && (
                    <div className='p-5 w-full'>
                        <div className='bg-white mb-2 rounded-md'>
                            <div className='p-5 flex justify-between items-center border-b'>
                                <div className='flex justify-start gap-4 items-center'>
                                    <p style={{ fontWeight: 600, fontSize: '16px' }}>{t('collections')}</p>
                                </div>
                            </div>
                            <div className='pt-3'>
                                <AssignedCollections collections={collections ?? []} user={user} />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </AppLayout>
        </>
    )

}

export default AvailableLoadsExternal
